import { NodeStatus } from 'components';
import { usePerms } from 'contexts/PermsContext';
import dayjs from 'dayjs';
import { hasDevicePermissionFromDevice } from 'features/integrations/features/ConnectedSystems/utils/devicePermissionUtils';
import { selectSystemCount } from 'features/integrations/state/selectors/integrationSelectors';
import {
  openAwareSettings,
  openDeviceSettings,
  openUSGSSettings,
} from 'features/integrations/state/slices/deviceSlice';
import { DeviceIsSystemType } from 'features/integrations/utils/deviceComparisionUtils';
import { FaCog } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { deviceImageList } from 'utils/iconUtils';

export function DeviceRow({ deviceId, last, hideDate = false }) {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const systemCount = useSelector(selectSystemCount);
  const perms = usePerms();
  const dispatch = useDispatch();
  const deviceIndex = connectedDevices?.findIndex((device) => device.id === deviceId);

  const device = deviceIndex || deviceIndex === 0 ? connectedDevices[deviceIndex] : null;

  if (!device) return null;
  const canEditDevice = hasDevicePermissionFromDevice(perms, device, 'createEdit');

  // Compact layout when date column is hidden (used in group dropdowns).
  const nameColClass = hideDate ? 'col-12 col-md-4 col-lg-5' : 'col-12 col-md-4 col-lg-3';
  const statusColClass = hideDate ? 'col-12 col-md-3 col-lg-4' : 'col-12 col-md-2 col-lg-2';
  const dateColClass = 'col-12 col-md-2 col-lg-3';
  const cogColClass = hideDate
    ? 'col-12 col-md-2 col-lg-1 d-flex justify-content-md-end'
    : 'col-12 col-md-1 col-lg-2 d-flex justify-content-md-end';

  const onSettingsClick = (e) => {
    e.stopPropagation();
    if (DeviceIsSystemType(device, 'Flood')) {
      dispatch(openAwareSettings(device.id));
    } else if (device.system_type === 'USGS') {
      dispatch(openUSGSSettings(device.id));
    } else {
      dispatch(openDeviceSettings(device.id));
    }
  };

  return (
    <div
      className={`row py-2 px-2 g-2 align-items-center ${
        last ? '' : 'border-bottom border-secondary'
      }`}
    >
      <div className={nameColClass}>
        <div className="d-flex align-items-center gap-3">
          <div>
            {deviceImageList[device?.device_type] ? (
              <img
                src={deviceImageList[device?.device_type]}
                alt={device?.device_type}
                width={'30px'}
              />
            ) : (
              <img src={deviceImageList['RESCALERT']} alt={'Rescalert'} width={'30px'} />
            )}
          </div>
          <div>
            <p className="m-0">{device?.alias || device?.monitoring_location_name || device?.name}</p>
            <p className="d-none d-lg-block m-0 text-secondary">{device?.name}</p>
          </div>
        </div>
      </div>
      <div className="col-12 col-md-3 col-lg-2">
        <p>{`${deviceIndex + 1} of ${systemCount?.connected_devices} Available`}</p>
      </div>
      <div className={statusColClass}>
        <NodeStatus device={device} />
      </div>
      {!hideDate && (
        <div className={dateColClass}>
          <p>{dayjs(device?.date_created).format('MM/DD/YYYY, hh:mm:ss A')}</p>
        </div>
      )}
      {canEditDevice ? (
        <div className={cogColClass}>
          <div onClick={onSettingsClick}>
            <FaCog size={22} />
          </div>
        </div>
      ) : (
        <div className={cogColClass.split(' d-flex')[0]} />
      )}
    </div>
  );
}

export function DeviceRowVertical({ deviceId, last }) {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const systemCount = useSelector(selectSystemCount);
  const perms = usePerms();
  const dispatch = useDispatch();
  const deviceIndex = connectedDevices?.findIndex((device) => device.id === deviceId);

  const device = deviceIndex || deviceIndex === 0 ? connectedDevices[deviceIndex] : null;

  if (!device) return null;
  const canEditDevice = hasDevicePermissionFromDevice(perms, device, 'createEdit');

  const onSettingsClick = (e) => {
    e.stopPropagation();
    if (DeviceIsSystemType(device, 'Flood')) {
      dispatch(openAwareSettings(device.id));
    } else if (device.system_type === 'USGS') {
      dispatch(openUSGSSettings(device.id));
    } else {
      dispatch(openDeviceSettings(device.id));
    }
  };

  return (
    <div className={`py-1 ${last ? '' : 'border-bottom border-secondary'}`}>
      <div>
        <div className="d-flex align-items-baseline">
          <div>
            {deviceImageList[device?.device_type] ? (
              <img
                src={deviceImageList[device?.device_type]}
                alt={device?.device_type}
                width={'30px'}
              />
            ) : (
              <img src={deviceImageList['RESCALERT']} alt={'Rescalert'} width={'30px'} />
            )}
          </div>
          <div>
            <p className="ms-2">{device?.alias || device?.monitoring_location_name || device?.name}</p>
          </div>
        </div>
      </div>
      <div>
        <p>
          Device Licenses:
          <br />
          {` ${deviceIndex + 1} of ${systemCount?.connected_devices} Available`}
        </p>
      </div>
      <div>
        <p>
          Date Created:
          <br />
          {dayjs(device?.date_created).format('MM/DD/YYYY, hh:mm:ss A')}
        </p>
      </div>
      <div className="d-flex justify-content-center">
        <NodeStatus device={device} />
      </div>
      {canEditDevice && (
        <div className="d-flex justify-content-end" onClick={onSettingsClick}>
          <FaCog size={22} />
        </div>
      )}
    </div>
  );
}
